import { useState } from 'react';
import { X, Key, Lock, Shield } from 'lucide-react';

/**
 * Change-password modal triggered from the user menu in the top-bar.
 * Calls /api/admin/auth/change-password with current + new + confirm.
 * Extracted from AdminLayout.jsx during the iter175 layout refactor.
 */
export function ChangePasswordModal({ onClose, token }) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPw.length < 8) { setError('New password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { setError('Passwords do not match'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPw, new_password: newPw, confirm_password: confirmPw }),
      });
      const text = await res.text();
      let data = {};
      try { data = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok) {
        setSuccess(true);
        setTimeout(onClose, 2000);
      } else {
        setError(data.detail || 'Failed to change password');
      }
    } catch {
      setError('Network error');
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }} onClick={onClose} />
      <div data-testid="change-password-modal" style={{
        position: 'relative', width: '420px', maxWidth: '95vw',
        background: 'var(--hs-card)', borderRadius: '14px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)', border: '1px solid var(--hs-border)',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--hs-border)', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: 36, height: 36, borderRadius: '10px', background: '#3B82F615', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Key size={18} style={{ color: '#3B82F6' }} />
          </div>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--hs-text-dark)', margin: 0 }}>Change Password</h2>
            <p style={{ fontSize: '11px', color: 'var(--hs-text-faint)', margin: 0 }}>Update your account password</p>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'var(--hs-hover)', border: 'none', borderRadius: '6px', padding: '5px', cursor: 'pointer', color: 'var(--hs-text-faint)' }}>
            <X size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {success ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#22C55E15', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <Lock size={22} style={{ color: '#22C55E' }} />
              </div>
              <p style={{ fontSize: '15px', fontWeight: 700, color: '#22C55E' }}>Password Changed</p>
              <p style={{ fontSize: '12px', color: 'var(--hs-text-faint)' }}>Closing...</p>
            </div>
          ) : (
            <>
              {error && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '8px', background: '#EF444410', border: '1px solid #EF444430', marginBottom: '16px' }}>
                  <Shield size={14} style={{ color: '#EF4444' }} />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#EF4444' }}>{error}</span>
                </div>
              )}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--hs-text-light)', marginBottom: '6px' }}>Current Password</label>
                <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} required autoFocus data-testid="current-password-input"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--hs-border)', background: 'var(--hs-input-bg)', color: 'var(--hs-text)', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--hs-text-light)', marginBottom: '6px' }}>New Password</label>
                <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required minLength={8} data-testid="new-password-input"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--hs-border)', background: 'var(--hs-input-bg)', color: 'var(--hs-text)', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--hs-text-light)', marginBottom: '6px' }}>Confirm New Password</label>
                <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required data-testid="confirm-password-input"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--hs-border)', background: 'var(--hs-input-bg)', color: 'var(--hs-text)', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <button type="submit" disabled={saving} data-testid="change-password-submit"
                style={{ width: '100%', padding: '11px', borderRadius: '8px', background: '#3B82F6', color: '#fff', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Changing...' : 'Change Password'}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
